/**
 * CSV parse, header mapping, and in-file checks for homeowner import.
 * PapaParse handles quoted commas, UTF-8 BOM, and CRLF.
 */

import Papa from 'papaparse';
import { normalizeHomeowner, splitFullName } from './homeownerIntake';

const CSV_MAX_ROWS = 500;
const CSV_MAX_BYTES = 2 * 1024 * 1024;
const IMPORT_CHUNK_SIZE = 50;

const IMPORT_FIELDS = [
  { key: 'first_name', label: 'First name', required: true },
  { key: 'last_name', label: 'Last name', required: true },
  { key: 'email', label: 'Email', required: true },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'address', label: 'Street address', required: false },
  { key: 'city', label: 'City', required: false },
  { key: 'state', label: 'State', required: false },
  { key: 'zip_code', label: 'ZIP code', required: false },
  { key: 'property_type', label: 'Property type', required: false },
  { key: 'construction_type', label: 'Construction', required: false },
  { key: 'exterior_finish', label: 'Exterior', required: false },
  { key: 'foundation_type', label: 'Foundation', required: false },
  { key: 'roof_material', label: 'Roof', required: false },
  { key: 'roof_year', label: 'Roof year', required: false },
  { key: 'hvac_count', label: 'HVAC count', required: false },
  { key: 'features', label: 'Features', required: false },
  { key: 'year_built', label: 'Year built', required: false },
  { key: 'square_feet', label: 'Square feet', required: false },
  { key: 'notes', label: 'Notes', required: false },
  { key: 'external_id', label: 'External id', required: false },
  { key: 'full_name', label: 'Full name (splits into first and last)', required: false },
];

const HEADER_ALIASES = {
  'first name': 'first_name',
  firstname: 'first_name',
  'given name': 'first_name',
  fname: 'first_name',
  'last name': 'last_name',
  lastname: 'last_name',
  surname: 'last_name',
  'family name': 'last_name',
  lname: 'last_name',
  'full name': 'full_name',
  name: 'full_name',
  email: 'email',
  'email address': 'email',
  'e-mail': 'email',
  'e mail': 'email',
  phone: 'phone',
  'phone number': 'phone',
  mobile: 'phone',
  cell: 'phone',
  'cell phone': 'phone',
  address: 'address',
  street: 'address',
  'street address': 'address',
  'address 1': 'address',
  'address line 1': 'address',
  city: 'city',
  town: 'city',
  state: 'state',
  st: 'state',
  zip: 'zip_code',
  'zip code': 'zip_code',
  zipcode: 'zip_code',
  'postal code': 'zip_code',
  postal: 'zip_code',
  'property type': 'property_type',
  type: 'property_type',
  'home type': 'property_type',
  construction: 'construction_type',
  'construction type': 'construction_type',
  exterior: 'exterior_finish',
  'exterior finish': 'exterior_finish',
  siding: 'exterior_finish',
  foundation: 'foundation_type',
  'foundation type': 'foundation_type',
  roof: 'roof_material',
  'roof material': 'roof_material',
  'roof type': 'roof_material',
  'roof year': 'roof_year',
  'roof age': 'roof_year',
  'year roof installed': 'roof_year',
  'hvac count': 'hvac_count',
  'ac count': 'hvac_count',
  'air conditioners': 'hvac_count',
  features: 'features',
  feature: 'features',
  amenities: 'features',
  'year built': 'year_built',
  built: 'year_built',
  'square feet': 'square_feet',
  'sq ft': 'square_feet',
  sqft: 'square_feet',
  'square footage': 'square_feet',
  notes: 'notes',
  note: 'notes',
  comments: 'notes',
  'external id': 'external_id',
  'crm id': 'external_id',
  'contact id': 'external_id',
};

function normalizeHeader(header) {
  return String(header || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function autoMatchHeaders(headers) {
  const mapping = {};
  IMPORT_FIELDS.forEach((field) => {
    mapping[field.key] = null;
  });
  const used = new Set();
  (headers || []).forEach((header) => {
    const field = HEADER_ALIASES[normalizeHeader(header)];
    if (!field || used.has(field)) return;
    mapping[field] = header;
    used.add(field);
  });
  return mapping;
}

function mappingMissingRequired(mapping) {
  const missing = [];
  const hasName = mapping.first_name && mapping.last_name;
  const hasFull = Boolean(mapping.full_name);
  if (!hasName && !hasFull) {
    if (!mapping.first_name) missing.push('first_name');
    if (!mapping.last_name) missing.push('last_name');
  } else {
    if (!mapping.first_name && !hasFull) missing.push('first_name');
    if (!mapping.last_name && !hasFull) missing.push('last_name');
  }
  if (!mapping.email) missing.push('email');
  return missing;
}

function applyMapping(row, mapping) {
  const out = {};
  IMPORT_FIELDS.forEach((field) => {
    if (field.key === 'full_name') return;
    const header = mapping[field.key];
    if (!header) return;
    out[field.key] = row[header];
  });
  if (mapping.full_name && (!mapping.first_name || !mapping.last_name)) {
    const split = splitFullName(row[mapping.full_name]);
    if (!mapping.first_name) out.first_name = split.first_name;
    if (!mapping.last_name) out.last_name = split.last_name;
  }
  return out;
}

function byteLength(text) {
  let bytes = 0;
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4;
      i += 1;
    } else bytes += 3;
  }
  return bytes;
}

function parseHomeownerCsv(text) {
  const raw = String(text == null ? '' : text);
  const bytes = byteLength(raw);
  if (bytes > CSV_MAX_BYTES) {
    return { ok: false, code: 'file_too_large', message: 'CSV files must be 2 MB or smaller.', headers: [], rows: [] };
  }
  const cleaned = raw.replace(/^\uFEFF/, '');
  const parsed = Papa.parse(cleaned, {
    header: true,
    skipEmptyLines: 'greedy',
  });
  const headers = (parsed.meta.fields || []).filter((header) => String(header || '').trim() !== '');
  const fatal = (parsed.errors || []).find((err) => err.type === 'Quotes' || err.code === 'UndetectableDelimiter');
  if (fatal && (!parsed.data || parsed.data.length === 0)) {
    return { ok: false, code: 'parse_error', message: 'Could not read that CSV. Save the sheet as CSV and try again.', headers, rows: [] };
  }
  if (parsed.data.length > CSV_MAX_ROWS) {
    return {
      ok: false,
      code: 'too_many_rows',
      message: `CSV imports are limited to ${CSV_MAX_ROWS} homeowners.`,
      headers,
      rows: [],
    };
  }
  const rows = parsed.data.map((row, index) => ({
    rowNumber: index + 2,
    values: row,
  }));
  return { ok: true, headers, rows, parseErrors: parsed.errors || [] };
}

/**
 * Validate mapped rows and flag duplicate emails inside the file.
 * The first copy is kept. Later copies are errors and must not be imported.
 */
function validateMappedRows(mappedRows, options = {}) {
  const seen = new Map();
  return mappedRows.map((row, index) => {
    const rowNumber = row.rowNumber || index + 2;
    const normalized = normalizeHomeowner(row.values || row, options);
    const email = normalized.data?.email || String((row.values || row).email || '').trim().toLowerCase();
    let status = normalized.ok ? 'new' : 'error';
    const errors = { ...normalized.errors };
    if (normalized.ok && email) {
      if (seen.has(email)) {
        status = 'error';
        errors.email = `Duplicate of row ${seen.get(email)} in this file`;
      } else {
        seen.set(email, rowNumber);
      }
    }
    return {
      rowNumber,
      status,
      errors,
      warnings: normalized.warnings,
      data: normalized.ok && status !== 'error' ? normalized.data : null,
      values: row.values || row,
    };
  });
}

function chunkRows(rows, size = IMPORT_CHUNK_SIZE) {
  const chunks = [];
  const list = rows || [];
  for (let i = 0; i < list.length; i += size) {
    chunks.push(list.slice(i, i + size));
  }
  return chunks;
}

const CLIENT_TEMPLATE_FILENAME = 'pacarion-client-upload-template.csv';

function buildTemplateCsv() {
  return Papa.unparse({
    fields: [
      'first_name', 'last_name', 'email', 'phone', 'address', 'city', 'state',
      'zip_code', 'property_type', 'construction_type', 'exterior_finish', 'foundation_type',
      'roof_material', 'roof_year', 'hvac_count', 'features', 'year_built', 'square_feet', 'notes', 'external_id',
    ],
    data: [
      ['Pat', 'Lee', 'pat.lee@leehome.net', '555-0100', '123 Main St, Orlando', 'Orlando', 'FL', '32801', 'single_family', 'CBS', 'stucco', 'slab', 'concrete tile', '2015', '1', 'pool|irrigation', '1998', '1800', 'Gate code is on file', 'crm-1001'],
      ['Jordan', 'Smith', 'jordan.smith@smithhome.net', '555-0199', '456 Oak Ave, Tampa', 'Tampa', 'FL', '33602', 'townhouse', 'wood frame', 'vinyl siding', 'crawlspace', 'asphalt shingle', '2006', '2', 'solar|garage', '2004', '1420', '', 'crm-1002'],
    ],
  });
}

function rowsToCsv(rows) {
  if (!rows || rows.length === 0) return '';
  const fields = Object.keys(rows[0]);
  return Papa.unparse({ fields, data: rows.map((row) => fields.map((field) => row[field] == null ? '' : row[field])) });
}

export {
  CSV_MAX_ROWS,
  CSV_MAX_BYTES,
  IMPORT_CHUNK_SIZE,
  IMPORT_FIELDS,
  normalizeHeader,
  autoMatchHeaders,
  mappingMissingRequired,
  applyMapping,
  parseHomeownerCsv,
  validateMappedRows,
  chunkRows,
  buildTemplateCsv,
  rowsToCsv,
  CLIENT_TEMPLATE_FILENAME,
};
